import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { App, Button, Card, Space, Spin } from 'antd';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SharedTransportForm from '../components/SharedTransportForm';
import './TransportRouteFormPage.css';
import { omitUnchangedSharedTransportAllocationFields } from '../components/sharedTransportFormUtils';
import { normalizeOrderOptions, type OrderOptionDto } from '../dto/order';
import type { SupplierOrderDto } from '../dto/supplierOrder';
import { fetchSupplierOrders } from '../modules/orders/api/supplierOrdersApi';
import { type TransportRouteDetailDto } from '../dto/transport';
import { canManageTransportRoutes } from '../modules/transports/transportRouteAccessPolicy';
import { getTransportErrorMessage, isBlockingTransportError } from '../modules/transports/transportErrorMessages';
import { useFeedback } from '../utils/feedback';
import api from '../services/api';
import { authService } from '../services/auth';
import { submitWithOperationalException } from '../modules/operationalExceptions/operationalExceptionFlow';
import { fetchTransportRoute, updateTransportRoute } from '../modules/transports/api/transportRoutesApi';
import { buildOrderTransportPath, readTransportReturnContext } from '../modules/transports/transportReturnContext';

const EditTransportRoute: React.FC = () => {
  const { t } = useTranslation(['transport', 'common']);
  const feedback = useFeedback();
  const { modal } = App.useApp();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const orderIdToLink = searchParams.get('orderId');
  const returnContext = readTransportReturnContext(searchParams);
  const [availableOrders, setAvailableOrders] = useState<OrderOptionDto[]>([]);
  // Etap 2 (transport przywozowy): candidates for the inbound (ZD) link.
  const [availableSupplierOrders, setAvailableSupplierOrders] = useState<SupplierOrderDto[]>([]);
  const [supplierOrdersLoading, setSupplierOrdersLoading] = useState(true);
  const [supplierOrdersLoadError, setSupplierOrdersLoadError] = useState(false);
  const [transport, setTransport] = useState<TransportRouteDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const currentUser = authService.getCurrentUser();
  const canManageTransports = canManageTransportRoutes(currentUser);
  const canEditTransportCosts = authService.canEditTransportCosts();
  const canViewTransportCosts = authService.canViewTransportCosts();

  const fetchTransport = useCallback(async (transportId: string) => {
    try {
      setTransport(await fetchTransportRoute(transportId));
    } catch (error) {
      feedback.error(t('transport:errorFetchingRoute'));
      navigate('/app/transports');
    }
  }, [navigate, t, feedback]);

  const fetchOrders = useCallback(async () => {
    try {
      // Ask for supplier purchase-value hints so newly-added cargo lines can
      // prefill "Wartość towaru" from their linked supplier orders. The backend
      // strips the hints for users without supplier-order purchase-price
      // visibility, so requesting them unconditionally is safe.
      const response = await api.get('/orders', {
        params: { includeSupplierPurchaseHints: 'true' },
      });
      setAvailableOrders(normalizeOrderOptions(response.data, 'transport.edit.availableOrders'));
    } catch (error) {
      feedback.error(t('transport:errorFetchingOrders'));
    } finally {
      setFetching(false);
    }
  }, [t, feedback]);

  // Etap 2: advisory fetch — an empty candidate list still lets the operator
  // see (and keep) the inbound links already saved on this transport.
  const fetchSupplierOrdersForLinking = useCallback(async () => {
    setSupplierOrdersLoading(true);
    setSupplierOrdersLoadError(false);
    try {
      setAvailableSupplierOrders(await fetchSupplierOrders());
    } catch (error) {
      setAvailableSupplierOrders([]);
      setSupplierOrdersLoadError(true);
    } finally {
      setSupplierOrdersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!id) {
      return;
    }

    if (!canManageTransports) {
      navigate('/app/transports', { replace: true });
      return;
    }

    void Promise.all([fetchTransport(id), fetchOrders(), fetchSupplierOrdersForLinking()]);
  }, [canManageTransports, fetchOrders, fetchSupplierOrdersForLinking, fetchTransport, id, navigate]);

  const handleSubmit = async (payload: Record<string, unknown>) => {
    if (!id) {
      return;
    }

    setLoading(true);

    try {
      const updatePayload = transport
        ? omitUnchangedSharedTransportAllocationFields(payload, transport)
        : payload;
      const response = await submitWithOperationalException(
        (confirmedPayload) => updateTransportRoute(id, confirmedPayload),
        updatePayload,
        modal,
        t,
      );
      if (!response) return;
      feedback.success(t('transport:routeUpdatedSuccessfully', {
        number: transport?.routeNumber,
      }));
      navigate(returnContext ? buildOrderTransportPath(returnContext.orderId) : `/app/transports/${id}`);
    } catch (error: any) {
      feedback.error(
        getTransportErrorMessage(error, t, 'transport:errorUpdatingRoute', {
          number: transport?.routeNumber,
        }),
        { sticky: isBlockingTransportError(error) },
      );
    } finally {
      setLoading(false);
    }
  };

  if (!canManageTransports) {
    return null;
  }

  if (fetching || !transport) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="transport-route-form-page">
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(returnContext ? buildOrderTransportPath(returnContext.orderId) : `/app/transports/${id}`)}>
          {t('common:back')}
        </Button>
      </Space>

      <Card className="transport-route-form-shell-card" title={t('transport:editRoute')}>
        <SharedTransportForm
          availableOrders={availableOrders}
          availableSupplierOrders={availableSupplierOrders}
          supplierOrdersLoading={supplierOrdersLoading}
          supplierOrdersLoadError={supplierOrdersLoadError}
          onRetrySupplierOrders={fetchSupplierOrdersForLinking}
          transport={transport}
          initialOrderIds={orderIdToLink ? [orderIdToLink] : undefined}
          canEditTransportCosts={canEditTransportCosts}
          canViewTransportCosts={canViewTransportCosts}
          loading={loading}
          onCancel={() => navigate(returnContext ? buildOrderTransportPath(returnContext.orderId) : `/app/transports/${id}`)}
          onSubmit={handleSubmit}
        />
      </Card>
    </div>
  );
};

export default EditTransportRoute;
